import { H3, SEEDANCE_FAST } from './provider-contracts';
import {
  MODELS,
  MAX_GENERATION_SECONDS,
  MAX_TAKE_SOURCE_SECONDS,
  reserveFor,
  withHandles,
  type ShotSpec,
} from './index';

export interface TakeSegment {
  index: number;
  start: number;
  duration: number;
  end: number;
  final: boolean;
}

/** One separately journalled paid request per beat. Source time includes only the opening handle. */
export function takeSegments(shot: ShotSpec): TakeSegment[] {
  const source = shot.generationDuration ?? withHandles(shot).generationDuration!;
  if (shot.duration <= MAX_GENERATION_SECONDS)
    return [{ index: 0, start: 0, duration: source, end: source, final: true }];
  const beats = shot.takeBeats;
  if (
    !beats ||
    source < shot.duration ||
    source > MAX_TAKE_SOURCE_SECONDS ||
    Math.abs(beats.reduce((n, b) => n + b.duration, 0) - shot.duration) > 0.01
  )
    throw new Error('Long takes need timed performance beats covering their full duration.');
  if (
    beats.length < 2 ||
    beats.length > 4 ||
    beats.some((b) => b.duration < 3 || b.duration > 9.5) ||
    JSON.stringify(beats.flatMap((b) => b.dialogue)) !== JSON.stringify(shot.dialogue)
  )
    throw new Error('Take beats must preserve all dialogue inside bounded performance windows.');
  const head = shot.trimIn ?? withHandles(shot).trimIn!;
  const tail = source - head - shot.duration;
  if (tail < -0.01)
    throw new Error('Take source does not cover its opening handle and retained duration.');
  if (!Number.isInteger(source))
    throw new Error('Extended take source durations must use whole seconds.');
  let start = 0;
  let idealEnd = 0;
  return beats.map((beat, index) => {
    const final = index === beats.length - 1;
    idealEnd += beat.duration + (index === 0 ? head : 0) + (final ? tail : 0);
    // The live API rounded a 7.5-second request to eight seconds. Allocate whole-second
    // boundaries cumulatively, keeping the complete source length and opening handle.
    const end = final ? source : Math.ceil(Math.round(idealEnd * 100) / 100);
    const duration = end - start;
    if (duration < 3 || duration > MAX_GENERATION_SECONDS)
      throw new Error('Each take extension must fit the provider’s 3–10 second request limit.');
    const segment = { index, start, duration, end, final };
    start = end;
    return segment;
  });
}

export function assertTakeModel(shot: ShotSpec, model: string) {
  if (model === H3) {
    const source = shot.generationDuration ?? shot.duration;
    if (!Number.isInteger(source) || source < 4 || source > 15 || shot.duration > 15)
      throw new Error('H3 renders one sequence of 4–15 whole seconds; extensions are not used.');
    return;
  }
  if (
    model === SEEDANCE_FAST &&
    (shot.duration > 10 || (shot.generationDuration ?? shot.duration) > 12)
  )
    throw new Error(
      'Seedance Fast currently supports single shots up to ten seconds; extensions are not implemented.',
    );
  if (model === MODELS.controlledVideo && shot.duration > 8)
    throw new Error(
      'Veo controlled shots must be 8 seconds or shorter. Use Omni for this episode’s longer takes.',
    );
}

/** Reserve for cumulative output and prior-video context at each extension, rather than one clip. */
export function takeAllowance(shot: ShotSpec, model: string) {
  assertTakeModel(shot, model);
  if (shot.duration <= MAX_GENERATION_SECONDS)
    return reserveFor('VIDEO', model, shot.generationDuration ?? shot.duration);
  return takeSegments(shot).reduce((n, s) => n + reserveFor('VIDEO', model, s.end), 0);
}
